
import { useEffect } from 'react';
import Header from '@/components/Header';
import FooterSection from '@/components/FooterSection';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Link } from 'react-router-dom';

interface PackageFeature {
  name: string;
}

interface PricingPackage {
  name: string;
  features: string[];
  length: string;
  highlight?: boolean;
}

const StrategyPackages = () => {
  useEffect(() => {
    // Update document title
    document.title = 'Strategy Packages | DocketLabs';
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'AI strategy consulting packages for law firms. Choose from Foundation, Accelerated or Comprehensive plans. Start your journey.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'AI strategy consulting packages for law firms. Choose from Foundation, Accelerated or Comprehensive plans. Start your journey.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
    
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, []);

  const packages: PricingPackage[] = [
    {
      name: "Foundations Strategy",
      features: [
        "1-hour Discovery Session",
        "Full Firm Audit and Needs Analysis",
        "High-Level AI Adoption Plan (Short-Form)",
        "1 Follow-Up Advisory Call"
      ],
      length: "~2 weeks total"
    },
    {
      name: "Accelerated Strategy",
      features: [
        "1-hour Discovery Session",
        "Full Firm Audit and Needs Analysis",
        "Full AI Roadmap for Priority Adoption",
        "Risk & Compliance Guidelines",
        "Targeted Workflow Enhancements",
        "2 Follow-Up Advisory Sessions",
        "Priority Email Support (30 days)"
      ],
      length: "~4–6 weeks total",
      highlight: true
    },
    {
      name: "Comprehensive Strategy ⭐",
      features: [
        "90-min Discovery Workshop",
        "Full Firm Audit and Needs Analysis",
        "Comprehensive AI Strategy Roadmap",
        "Role-Based AI Adoption Plans",
        "Change Management Strategy",
        "Integration Guidance",
        "Implementation Coaching",
        "4 Follow-Up Advisory Sessions",
        "Advisory Support (90 days)"
      ],
      length: "~3–4 months total"
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow bg-background text-foreground">
        <section className="container mx-auto py-32 px-4">
          {/* Page Header with Updated Styling */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-blue-400 to-indigo-500 bg-clip-text text-transparent">
                AI Strategy Packages
              </span>
            </h1>
            <p className="text-lg text-gray-400">
              Choose the package that best fits your firm's AI adoption journey
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages.map((pkg, index) => (
              <Card 
                key={index}
                className={`flex flex-col h-full overflow-hidden border ${
                  pkg.highlight ? 'border-blue-500 bg-blue-50/5' : 'bg-card'
                }`}
              >
                <CardHeader className="pb-0">
                  <h3 className="text-xl font-bold">{pkg.name}</h3>
                </CardHeader>
                <CardContent className="flex-grow pt-6">
                  <div className="space-y-4">
                    <div>
                      <div className="font-medium mb-2 text-gray-400">What's Included</div>
                      <ul className="space-y-2">
                        {pkg.features.map((feature, fIndex) => (
                          <li key={fIndex} className="flex items-start gap-2">
                            <div className="rounded-full bg-green-500/20 p-1 mt-0.5">
                              <Check className="h-3 w-3 text-green-500" />
                            </div>
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="font-medium mb-2 text-gray-400">Engagement Length</div>
                      <p className="text-sm">{pkg.length}</p>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-6 pb-6 border-t">
                  <Link to="/services/strategy/free-discovery-session" className="w-full">
                    <Button 
                      className={`w-full ${pkg.highlight ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                      variant={pkg.highlight ? "default" : "outline"}
                    >
                      Get Started
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            ))}
          </div>
          
          {/* Additional Information */}
          <div className="max-w-2xl mx-auto mt-20 text-center">
            <h2 className="text-2xl font-bold mb-4">Not sure which package is right for you?</h2>
            <p className="text-gray-400 mb-8">
              Schedule a free discovery call with our team to discuss your specific needs 
              and get personalized recommendations.
            </p>
            <Link to="/services/strategy/free-discovery-session">
              <Button size="lg">Book a Free Discovery Call</Button>
            </Link>
            <p className="text-sm text-gray-600 mt-4">
              No commitment — we'll help you find the right strategy path for your firm.
            </p>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
};

export default StrategyPackages;
